// VIDEO-EDITING — transient store for Marcus's caption styling proposal.
//
// `propose_video_captions` (backend dry-run) returns a single
// `{presetId, customStyle, rationale, signals}` proposal but does not
// persist anything. `useCompanionToolDispatch` lifts that result here so
// `MarcusCaptionsSuggestionsPanel` can render the proposal for the user
// to review before a follow-up `apply_video_edit_spec` (or the captions
// apply route) commits `{presetId, customStyle}` to the project.
//
// State machine (mirrors useBrollProposalsStore):
//   idle ──Generate──► generating ──propose result──► proposed
//                          │                            │
//                          └── 180s timeout ──► error   │ Apply
//                                                       ▼
//                                                   applying
//                                          apply result │
//                                                       ▼
//                                                    applied (10s)
//                                                       │ auto
//                                                       ▼
//                                                     idle ◄── Undo (60s)
//
// Intentionally NOT persisted — proposals + lastApplied are session-scoped.
import { create } from "zustand";

export type CaptionsProposalsStatus =
  | "idle"
  | "generating"
  | "proposed"
  | "applying"
  | "applied"
  | "error";

export type CaptionPresetId =
  | "trust-bar"
  | "hero-headline"
  | "word-by-word-accent"
  | "two-line-conversational"
  | "quote-card"
  | "sticker-bar"
  | "hook-body-stack"
  | "caption-lite";

export interface CaptionsCustomStyle {
  positionY?: number;
  fontSize?: number;
  color?: string;
  fontFamily?: string;
  fontWeight?: number;
}

export interface CaptionsProposalSignals {
  aspectRatio: "9:16" | "1:1" | "16:9" | "other";
  durationSec: number;
  segmentCount: number;
  meanWordsPerSegment: number;
  meanWordsPerSec: number;
  contentTone: "energetic" | "narrative" | "educational" | "ad";
  hasWordTimings: boolean;
  dominantBrandColor: string | null;
  productRefAttached: boolean;
  tier: "rules" | "rules+haiku";
}

export interface CaptionsProposal {
  presetId: CaptionPresetId;
  customStyle: CaptionsCustomStyle;
  rationale: string;
  signals: CaptionsProposalSignals;
}

export interface CaptionsAppliedSnapshot {
  projectId: string;
  appliedPresetId: CaptionPresetId | null;
  appliedCustomStyle: CaptionsCustomStyle | null;
  /** Snapshot taken before apply — used by Undo to restore the prior style. */
  priorPresetId: CaptionPresetId | null;
  priorCustomStyle: CaptionsCustomStyle | null;
  at: number;
}

interface CaptionsProposalsState {
  status: CaptionsProposalsStatus;
  projectId: string | null;
  sourceActionId: string | null;
  proposal: CaptionsProposal | null;
  transcriptStatus: "reused" | "generated" | "refreshed" | null;
  error: string | null;
  lastApplied: CaptionsAppliedSnapshot | null;

  beginGenerating: (projectId: string) => void;
  setProposal: (input: {
    projectId: string;
    sourceActionId: string | null;
    proposal: CaptionsProposal;
    transcriptStatus: "reused" | "generated" | "refreshed";
  }) => void;
  setEmptyProposal: (input: { projectId: string; note: string }) => void;
  setError: (projectId: string, error: string) => void;
  clearProposal: () => void;

  beginApplying: (input: {
    projectId: string;
    priorPresetId: CaptionPresetId | null;
    priorCustomStyle: CaptionsCustomStyle | null;
  }) => void;
  markApplied: (input: {
    projectId: string;
    appliedPresetId: CaptionPresetId | null;
    appliedCustomStyle: CaptionsCustomStyle | null;
    priorPresetId: CaptionPresetId | null;
    priorCustomStyle: CaptionsCustomStyle | null;
  }) => void;
  markApplyError: (projectId: string, error: string) => void;
  consumeLastApplied: () => CaptionsAppliedSnapshot | null;
  clearLastApplied: () => void;
}

export const useCaptionsProposalsStore = create<CaptionsProposalsState>(
  (set, get) => ({
    status: "idle",
    projectId: null,
    sourceActionId: null,
    proposal: null,
    transcriptStatus: null,
    error: null,
    lastApplied: null,

    beginGenerating: (projectId) =>
      set({
        status: "generating",
        projectId,
        proposal: null,
        error: null,
        // Starting a new propose run invalidates the prior undo window.
        lastApplied: null,
      }),

    setProposal: ({ projectId, sourceActionId, proposal, transcriptStatus }) =>
      set({
        status: "proposed",
        projectId,
        sourceActionId,
        proposal,
        transcriptStatus,
        error: null,
      }),

    setEmptyProposal: ({ projectId, note }) =>
      set({
        status: "error",
        projectId,
        proposal: null,
        error: note,
      }),

    setError: (projectId, error) =>
      set({
        status: "error",
        projectId,
        proposal: null,
        error,
      }),

    clearProposal: () =>
      set({
        status: "idle",
        proposal: null,
        error: null,
      }),

    beginApplying: ({ projectId }) =>
      set({
        status: "applying",
        projectId,
      }),

    markApplied: ({
      projectId,
      appliedPresetId,
      appliedCustomStyle,
      priorPresetId,
      priorCustomStyle,
    }) =>
      set({
        status: "applied",
        projectId,
        proposal: null,
        error: null,
        lastApplied: {
          projectId,
          appliedPresetId,
          appliedCustomStyle,
          priorPresetId,
          priorCustomStyle,
          at: Date.now(),
        },
      }),

    markApplyError: (projectId, error) =>
      set({
        status: "error",
        projectId,
        error,
      }),

    consumeLastApplied: () => {
      const snapshot = get().lastApplied;
      if (snapshot) set({ lastApplied: null, status: "idle" });
      return snapshot;
    },

    clearLastApplied: () => set({ lastApplied: null }),
  }),
);
